import React, { useState } from 'react';
import { sendContactMessage } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import { MessageCircle, Send, HelpCircle, Ticket, Info } from 'lucide-react';

const ContactOrganizerButton = ({ eventId, eventTitle }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageType, setMessageType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const messageTypes = [
    { id: 'info', label: 'Quiero más información', icon: Info, color: 'text-blue-500' },
    { id: 'tickets', label: 'Tengo dudas sobre mis boletos', icon: Ticket, color: 'text-orange-500' },
    { id: 'other', label: 'Otra consulta', icon: HelpCircle, color: 'text-purple-500' }
  ];

  const handleTypeSelect = (type) => {
    setMessageType(type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!messageType) {
      toast.error('Selecciona un tipo de consulta');
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await sendContactMessage(eventId, {
        event_id: eventId,
        message_type: messageType,
        message: formData.message,
        sender_name: formData.name,
        sender_email: formData.email,
        sender_phone: formData.phone || null
      });
      
      toast.success('¡Mensaje enviado! El organizador te contactará pronto.');
      setOpen(false);
      setMessageType('');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full h-12 border-2 border-primary/30 hover:border-primary hover:bg-primary/5"
          data-testid="contact-organizer-button"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          Contactar al Organizador
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            Contactar al Organizador
          </DialogTitle>
          <DialogDescription>
            Envía tu consulta sobre "{eventTitle}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Message Type Selection */}
          {!messageType ? (
            <div className="space-y-2">
              <Label>¿Qué tipo de consulta tienes?</Label>
              <div className="space-y-2">
                {messageTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleTypeSelect(type.id)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                  >
                    <type.icon className={`w-5 h-5 ${type.color}`} />
                    <span className="font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Selected Type Badge */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  {messageTypes.find(t => t.id === messageType)?.icon && (
                    React.createElement(messageTypes.find(t => t.id === messageType).icon, {
                      className: `w-4 h-4 ${messageTypes.find(t => t.id === messageType).color}`
                    })
                  )}
                  <span className="text-sm font-medium">
                    {messageTypes.find(t => t.id === messageType)?.label}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setMessageType('')}
                >
                  Cambiar
                </Button>
              </div>

              {/* Contact Form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="contact-name">Nombre Completo *</Label>
                  <Input
                    id="contact-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Tu nombre"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-email">Correo Electrónico *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="tu@email.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-phone">Teléfono (Opcional)</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+504 9999-9999"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-message">Mensaje *</Label>
                  <Textarea
                    id="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Escribe tu consulta aquí..."
                    rows={4}
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="send-message-button"
                >
                  {loading ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Mensaje
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactOrganizerButton;
